import { memo, useEffect, useState, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { first, isEmpty, isNil, isString, isUndefined, noop } from 'lodash';
// eslint-disable-next-line import/no-named-as-default
import List from 'react-virtualized/dist/commonjs/List';
// Import UI Components
import { Tooltip, ExtendedInput, BusyLoader, Option, Empty, Checkbox, Switcher } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';

const MenuItemDropdown = lazy(() => import('components/MenuItemDropdown'));

const CheckboxColumnKey = 'checkbox';

const OptionList = (props) => {
    const {
        columns,
        value,
        valueKey,
        isLoading,
        list,
        listWidth,
        listHeight,
        listRowHeight,
        rowCount,
        emptySize,
        isMultiselect,
        contentTop,
        onChange,
        defaultSearchValue,
        onSearch,
        showSelected,
        onChangeShowSelected,
        depthLevel,
    } = props;
    const { t } = useTranslation();

    const [searchValue, setSearchValue] = useState(
        columns
            .filter((x) => x.filterable)
            .reduce((acc, item) => {
                acc[item.key] = defaultSearchValue[item.key] ?? '';
                return acc;
            }, {}),
    );

    const rowRenderer = (item) => {
        if (!isUndefined(item)) {
            let tmpColumns = columns;
            if (isMultiselect) {
                tmpColumns = [{ key: CheckboxColumnKey }, ...columns];
            }
            return tmpColumns.map((column, index) => {
                const columnItemKey = column.key;

                if (isMultiselect && columnItemKey === CheckboxColumnKey) {
                    return (
                        <Checkbox
                            key={`${item[valueKey]}_${index}`}
                            checked={value.includes(item[valueKey])}
                            onChange={(e) => {
                                e.preventDefault();
                                onClickHandler(item, value.includes(item[valueKey]));
                            }}
                        />
                    );
                }

                return (
                    <Tooltip
                        key={`${item[valueKey]}_${index}`}
                        title={isString(columnItemKey) ? `${item[columnItemKey]}` : undefined}
                    >
                        <span className={`crm-option-column-${index} ellipsis-text`}>{item[columnItemKey]}</span>
                    </Tooltip>
                );
            });
        }
        return <></>;
    };

    const onClickHandler = (listItem, currentState) => {
        if (isMultiselect) {
            if (currentState) {
                onChange(value.filter((item) => item !== listItem[valueKey]));
            } else {
                onChange([...value, listItem[valueKey]]);
            }
        } else {
            onChange([listItem[valueKey]]);
        }
    };

    const handleMenuItemChange = (column) => (data) => {
        if ('value' in data) {
            const { value } = data;
            setSearchValue((prev) => ({ ...prev, [column.key]: value }));
        }
    };

    const filterChangeHandler = (column) => (e) => {
        handleMenuItemChange(column)(e.target);
    };

    useEffect(() => {
        onSearch(searchValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    return (
        <div className="crm-option-list-content" style={{ top: `${contentTop}px` }}>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {!(columns.length === 1 && !first(columns as any[]).filterable) && (
                <Option
                    leftCustomElement={
                        <>
                            {isMultiselect && <Checkbox className="crm-invisible" disabled={true} />}

                            {columns.map((column, index) => {
                                let component = (
                                    <span className={`crm-option-header-column-${index}`}>{column.label}</span>
                                );

                                if (column.filterable) {
                                    if (!isNil(column.menuItemId)) {
                                        component = (
                                            <Suspense fallback={null}>
                                                <MenuItemDropdown
                                                    menuItemId={column.menuItemId}
                                                    isMultiselect={true}
                                                    getUpdate={handleMenuItemChange(column)}
                                                    extendTargetWidth={false}
                                                    displayName={column.label}
                                                    placeholder={column.label}
                                                    doInitialRequest={true}
                                                    getDropdownData={column.setMenuItemOptions}
                                                    popoverAlign="start"
                                                    depthLevel={depthLevel + 1}
                                                    index={index}
                                                    disabled={showSelected}
                                                />
                                            </Suspense>
                                        );
                                    } else {
                                        component = (
                                            <ExtendedInput
                                                className="crm-option-list-header-item-input"
                                                canClear={true}
                                                disabled={showSelected}
                                                placeholder={column.label}
                                                value={searchValue[column.key]}
                                                label={column.label}
                                                labelAppearance="swap"
                                                onChange={filterChangeHandler(column)}
                                            />
                                        );
                                    }
                                }

                                return (
                                    <div key={column.key} className="crm-option-list-header-item">
                                        {component}
                                    </div>
                                );
                            })}
                        </>
                    }
                    border="bottom"
                />
            )}
            {isMultiselect &&
                columns.reduce((acc, item) => {
                    acc = acc || item.filterable;
                    return acc;
                }, false) && (
                    <Switcher
                        disabled={isLoading}
                        label={t(l.ShowSelected)}
                        checked={showSelected}
                        onChange={() => {
                            onChangeShowSelected(!showSelected);
                        }}
                    />
                )}
            <div style={{ height: `${listHeight}px` }}>
                <BusyLoader isBusy={isLoading}>
                    {!isEmpty(list) ? (
                        <List
                            width={listWidth}
                            height={listHeight}
                            rowCount={rowCount}
                            rowHeight={listRowHeight}
                            rowRenderer={({ index, style }) => {
                                return (
                                    <Option
                                        style={style}
                                        key={list[index][valueKey]}
                                        leftCustomElement={rowRenderer(list[index])}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onClickHandler(list[index], value.includes(list[index][valueKey]));
                                        }}
                                        active={value.includes(list[index][valueKey])}
                                    />
                                );
                            }}
                        />
                    ) : (
                        <Empty title={t(l.NoDataToDisplay)} size={emptySize} />
                    )}
                </BusyLoader>
            </div>
        </div>
    );
};

OptionList.propTypes = {
    columns: PropTypes.array,
    value: PropTypes.array,
    valueKey: PropTypes.string,
    isLoading: PropTypes.bool,
    list: PropTypes.array,
    listWidth: PropTypes.number,
    listHeight: PropTypes.number,
    listRowHeight: PropTypes.number,
    rowCount: PropTypes.number,
    emptySize: PropTypes.string,
    contentTop: PropTypes.number,
    isMultiselect: PropTypes.bool,
    onChange: PropTypes.func,
    onSearch: PropTypes.func,
    defaultSearchValue: PropTypes.object,
    showSelected: PropTypes.bool,
    onChangeShowSelected: PropTypes.func,
    depthLevel: PropTypes.number,
};

OptionList.defaultProps = {
    columns: [],
    value: [],
    valueKey: 'value',
    isLoading: false,
    listWidth: 200,
    listHeight: 240,
    listRowHeight: 40,
    rowCount: 0,
    emptySize: 'big',
    contentTop: 50,
    isMultiselect: false,
    onChange: noop,
    onSearch: noop,
    defaultSearchValue: {},
    showSelected: false,
    onChangeShowSelected: noop,
};

export default memo(OptionList);
